import React, { useState } from 'react';

import request from '@nineeng/request';

import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import WelcomeScreen from '../layout/WelcomeScreen.jsx';
import VATField from '../components/VATField.jsx';
import TextField from '../components/BasicMaterial.jsx';
import AutoCompleteByID from '../components/AutoCompleteByID.jsx';
import companyAnim from './assets/CompanySection.json';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import ContentContainer from '../layout/ContentContainer.jsx';

import { getTextDefaults } from './translations/companySectionTranslations';
import { countriesEUMain } from '../util/countries';

const WelcomePage = ({ text }) => {
	return (
		<WelcomeScreen animation={companyAnim} animationOptions={{ loop: false }}>
			<Typography variant='h6' sx={{ marginBottom: 2 }}>{text.welcomeTitle}</Typography>
			<Typography>{text.welcomeReason}</Typography>
		</WelcomeScreen>
	);
};

const VATPage = ({ values, text, onChange, isUnknownCompany, setCurrentScreen, disabled }) => {
	const Btn = <Button 
		endIcon={<ArrowForwardIcon />} 
		color='inherit'
		onClick={() => setCurrentScreen('details')}
	>
		{text.unknownCompanyBtn}
	</Button>;

	return (
		<ContentContainer>
			<Typography variant='body1' sx={{ marginBottom: 2 }}>{text.vatText}</Typography>
			<VATField
				label={text.lblVAT}
				onChange={onChange}
				name='vatNumber'
				value={values.vatNumber}
				disabled={disabled}
			/>
			{isUnknownCompany && <Alert 
				severity='error' 
				action={Btn}
			>
				{text.unknownCompanyText}
			</Alert>}
		</ContentContainer>
	);
};

const ProcessingPage = ({ text }) => {
	return (
		<WelcomeScreen>
			<Typography sx={{ textAlign: 'center' }}><CircularProgress /></Typography>
			<Typography sx={{ textAlign: 'center' }}>Processing...</Typography>
		</WelcomeScreen>
	);
};

const DetailsPage = ({ onChange, text, values, isUnknownCompany, disabled }) => {
	const hasCompanyId = !!values.companyId;

	return (
		<WelcomeScreen>
			<Typography variant='body1' sx={{ marginBottom: 2 }}>
				{isUnknownCompany ? text.detailsUnknownText : text.detailsText}
			</Typography>
			<TextField name='name' label="Name" value={values.name} onChange={onChange} disabled={hasCompanyId || disabled} required />
			<TextField name='addressLine1' label="Address" value={values.addressLine1} onChange={onChange} disabled={disabled} required />
			<TextField name='addressLine2' label="Address - Extra" value={values.addressLine2} disabled={disabled} onChange={onChange} />
			<TextField name='place' label="Place" value={values.place} onChange={onChange} disabled={disabled} required />
			<TextField name='postalCode' label="Postal Code" value={values.postalCode} disabled={disabled} onChange={onChange} required />
			<AutoCompleteByID
				name='country'
				label="Country"
				value={values.country}
				options={countriesEUMain}
				optionLabel='name'
				disabled={hasCompanyId || disabled} 
				onChange={onChange}
				required
			/>
		</WelcomeScreen>
	);
};

const FinishedPage = ({ text }) => {
	return (
		<WelcomeScreen>
			<Typography variant='h6' sx={{ marginBottom: 2 }}>{text.finishedTitle}</Typography>
			<Typography>{text.finishedReason}</Typography>
		</WelcomeScreen>
	);
};

const CompanySection = ({
	onChange = () => { },
	values: formValues = {},
	textOverride = {},
	background = 'light',
	navigationProps,
	selectedLanguage = 'EN',
	options = {},
	name = 'groupProps',
	companyVatUrl = '/api/validate/vat',
	disabled = false,
	...rest
}) => {
	const [isUnknownCompany, setUnknownCompany] = useState(false);
	const textDefaults = getTextDefaults(selectedLanguage);
	const text = Object.assign({}, textDefaults, textOverride);
	const defaultPage = 'details';
	const values = formValues[name] || {};

	const checkDetails = () => {
		return !!(values.name && values.addressLine1 && values.place && values.postalCode && values.country);
	};
	
	const screens = {
		welcome: {
			previous: () => navigationProps.previous(),
			next: () => setCurrentScreen('vat'),
			component: WelcomePage,
			canContinue: true,
		},
		vat: {
			previous: () => setCurrentScreen('welcome'),
			next: () => handleVAT(values.vatNumber),
			component: VATPage,
			canContinue: !!values.vatNumber,
		},
		processing: {
			component: ProcessingPage,
		},
		details: {
			previous: () => setCurrentScreen('vat'),
			next: () => setCurrentScreen('finished'),
			component: DetailsPage,
			canContinue: checkDetails(),
		},
		finished: {
			previous: () => setCurrentScreen('details'),
			next: (event) => navigationProps.next(event),
			component: FinishedPage,
			canContinue: true,
		},
	};

	const handleVAT = async vatNumber => {
		try {
			setCurrentScreen('processing');
			const url = `${companyVatUrl}/${vatNumber}`;
			const { body } = await request.get(url);
			console.log(body);
			onChange({ target: { name, value: body }});
			setCurrentScreen('details');
		} catch(error) {
			setCurrentScreen('vat');
			setUnknownCompany(true);
			console.error(error);
		}
	};

	const handleChange = ({ target }) => onChange({ target: { ...target, name: `${name}.${target.name}` }});

	const [currentScreen, setCurrentScreen] = useState(defaultPage);
	const screen = screens[currentScreen] || screens.welcome;
	const screenProps = { values, onChange: handleChange, text, isUnknownCompany, setCurrentScreen, disabled };

	return (
		<SectionContainer
			background={background}
			Navigation={
				<SectionNav
					{...navigationProps}
					hasPrevious={!!screen.previous}
					hasNext={currentScreen !== 'finished' || navigationProps.hasNext}
					canContinue={!!screen.canContinue}
					next={screen.next}
					previous={screen.previous}
					selectedLanguage={selectedLanguage}
				/>
			}
		>
			{screen && <screen.component {...screenProps} />}
		</SectionContainer>
	);
};

CompanySection.type = 'CompanySection';
CompanySection.hasNavigation = true;

export default CompanySection;